import { type CardAppearanceId, DesignGroup, resolveDesignSelection } from '@today/brand-config/runtime'
import { cn, useOptionalDesign } from '@today/ui-primitives'
import { type ReactElement, type ReactNode, useId } from 'react'

/** Surface treatment per `components.card` appearance (brand-design-configuration REQ-004). */
const APPEARANCE_CLASSES: Record<CardAppearanceId, string> = {
	bordered: 'border border-ds-border bg-ds-surface',
	flat: 'bg-ds-surface-muted',
	elevated: 'shadow-ds-card bg-ds-surface',
}

/**
 * Props for the {@link CardShell} molecule.
 *
 * @property appearance - `bordered` | `flat` | `elevated`; defaults to the bound brand's
 *   `components.card` selection, else `bordered` (brand-design-configuration REQ-004).
 * @property title - Optional header text; without it no header region renders. It also names the card region.
 * @property children - Card body.
 * @property footer - Optional footer node; without it no footer region renders.
 * @property className - Extra Tailwind classes merged onto the card.
 *
 * @example
 * ```tsx
 * <CardShell title="Summary" footer={<LinkAction href="/all">See all</LinkAction>}>
 *   <p>Body</p>
 * </CardShell>
 * ```
 */
export interface CardShellProps {
	appearance?: CardAppearanceId
	title?: string
	children: ReactNode
	footer?: ReactNode
	className?: string
}

/**
 * Props shared by every {@link CardShell} slot.
 *
 * @property children - Slot content.
 * @property className - Extra Tailwind classes merged onto the slot element.
 */
export interface CardShellSlotProps {
	children?: ReactNode
	className?: string
}

function CardShellMedia({ children, className }: CardShellSlotProps): ReactElement {
	return (
		<div data-slot="media" className={cn('overflow-hidden rounded-ds-md', className)}>
			{children}
		</div>
	)
}

function CardShellContent({ children, className }: CardShellSlotProps): ReactElement {
	return (
		<div data-slot="content" className={cn('flex flex-col gap-ds-2', className)}>
			{children}
		</div>
	)
}

function CardShellTitle({ children, className }: CardShellSlotProps): ReactElement {
	return (
		<h3 data-slot="title" className={cn('text-ds-base font-semibold text-ds-text', className)}>
			{children}
		</h3>
	)
}

function CardShellDescription({ children, className }: CardShellSlotProps): ReactElement {
	return (
		<p data-slot="description" className={cn('text-ds-sm text-ds-text-muted', className)}>
			{children}
		</p>
	)
}

function CardShellActions({ children, className }: CardShellSlotProps): ReactElement {
	return (
		// biome-ignore lint/a11y/useSemanticElements: an action cluster is a generic group, not a form fieldset.
		<div data-slot="actions" role="group" className={cn('flex flex-wrap gap-ds-2', className)}>
			{children}
		</div>
	)
}

/**
 * Bordered surface with optional header and footer regions and compound slots.
 *
 * @remarks
 * - **Prop-driven:** the header renders only with a `title` and the footer only
 *   with a `footer`, so the bare shell is a bordered body and nothing else.
 * - **Composition (architecture §15):** `CardShell.Media`, `.Content`, `.Title`,
 *   `.Description` and `.Actions` render in the order the consumer writes them,
 *   so a different arrangement needs no structural prop.
 * - **Presentational:** it arranges whatever nodes it is given; it knows nothing
 *   about what they represent.
 * - **Styling:** every colour, radius and spacing class is a `ds-*` token utility.
 *
 * @example
 * ```tsx
 * <CardShell>
 *   <CardShell.Media><img alt={imageAlt} src={src} /></CardShell.Media>
 *   <CardShell.Content>
 *     <CardShell.Title>{title}</CardShell.Title>
 *     <CardShell.Description>{description}</CardShell.Description>
 *   </CardShell.Content>
 *   <CardShell.Actions><Button onPress={onApply}>{applyLabel}</Button></CardShell.Actions>
 * </CardShell>
 * ```
 *
 * @see packages/ui-components/src/card-shell/card-shell.stories.tsx for visual examples
 */
function CardShellRoot({ appearance, title, children, footer, className }: CardShellProps): ReactElement {
	const titleId = useId()
	const design = useOptionalDesign()
	const resolved = appearance ?? (resolveDesignSelection(design, DesignGroup.Components, 'card') as CardAppearanceId)
	return (
		<section
			data-appearance={resolved}
			aria-labelledby={title ? titleId : undefined}
			className={cn('flex flex-col rounded-ds-lg', APPEARANCE_CLASSES[resolved], className)}
		>
			{title ? (
				<header data-testid="card-shell-header" className="border-b border-ds-border px-4 py-3">
					<h3 id={titleId} className="text-ds-base font-semibold text-ds-text">
						{title}
					</h3>
				</header>
			) : null}
			<div className="px-4 py-3 text-ds-base text-ds-text">{children}</div>
			{footer ? (
				<footer
					data-testid="card-shell-footer"
					className="border-t border-ds-border px-4 py-3 text-ds-sm text-ds-text-muted"
				>
					{footer}
				</footer>
			) : null}
		</section>
	)
}

export const CardShell = Object.assign(CardShellRoot, {
	Media: CardShellMedia,
	Content: CardShellContent,
	Title: CardShellTitle,
	Description: CardShellDescription,
	Actions: CardShellActions,
})
